import { useCallback, useEffect, useMemo } from 'react'

import { PointCloudOctree, Potree } from '@datalabs-jp/three-loader'
import { EDITOR_TOOLS } from '@editor/config/constants'
import { MaskRegion } from '@editor/interfaces/maskRegion'
import { Cuboid } from '@editor/interfaces/shape'
import { useAppSelector } from '@editor/store/app'
import { useFrame, useThree } from '@react-three/fiber'
import { NormalBlending, Vector3 } from 'three'
import { useThrottledCallback } from 'use-debounce'

const PotreePCD = ({
  pointClouds,
  potree,
  isPointCloudInvisible,
}: {
  pointClouds: PointCloudOctree[]
  potree: Potree
  isPointCloudInvisible: boolean
}) => {
  // Hooks
  const { gl, camera } = useThree()

  // Store
  const selectedTool = useAppSelector((state) => state.editor.selectedTool)
  const prevSelectedTool = useAppSelector((state) => state.editor.prevSelectedTool)

  const pcdTransparency = useAppSelector((state) => state.editorSettings.display.pcd.opacity)
  const isMeshFilePresent = useAppSelector((state) => state.solidMesh.isPresent)

  const workingMask = useAppSelector((state) => state.maskPCD.workingMask)
  const savingMask = useAppSelector((state) => state.maskPCD.savingMask)
  const maskRegions = useAppSelector((state) => state.maskPCD.regions)

  const showAsDim =
    EDITOR_TOOLS.PCD_TRIM_CUBOID === selectedTool ||
    (EDITOR_TOOLS.FOCUS === selectedTool && prevSelectedTool === EDITOR_TOOLS.PCD_TRIM_CUBOID)

  /**
   * Collect only static active masks (exclude working mask) to be applied to the material
   * as uniforms for masking in the shader. This is because the working mask is still being manipulated
   * and we don't want to use the Redux store as it may cause performance issues with frequent updates.
   */
  const activeMasks = useMemo(
    () =>
      [
        // prettier-ignore
        ...maskRegions,
        ...(savingMask || []),
      ].filter((region) => !region.invisible),
    [maskRegions, savingMask],
  )

  /**
   * Update the potree mask config with the current active masks and their opacities.
   * This will be used in the shader to determine the opacity of points based on their position relative to the masks.
   *
   * @param extraMask Additional mask to be applied on top of the active masks (e.g. working mask) without adding it to the Redux store.
   */
  const updateMask = useCallback(
    (extraMask: MaskRegion[]) => {
      const masks = [...activeMasks, ...extraMask].filter((mask) => !mask.invisible)

      // If there are masks, default to hiding points outside of masks. Otherwise, show all points.
      // On dimmed mode, force 0.1 opacity for better visual feedback to users.
      let defaultOpacity = masks.length > 0 ? 0 : pcdTransparency
      if (showAsDim) defaultOpacity = 0.1

      pointClouds.forEach((pointCloud) => {
        pointCloud.material.opacity = defaultOpacity
        pointCloud.material.enableTransparency()
        pointCloud.material.blending = NormalBlending
        pointCloud.material.depthTest = !isMeshFilePresent
      })

      potree.setMaskConfig({
        cuboids: masks.map((m) => ({
          id: m.region_id,
          center: new Vector3(...m.center),
          rotation: m.rotation,
          extent: new Vector3(...m.extent),
          opacity: pcdTransparency,
        })),
        defaultOpacity,
      })
    },
    [activeMasks, showAsDim, potree, pcdTransparency, isMeshFilePresent, pointClouds],
  )

  /**
   * Update mask config whenever active masks or their opacities change. This will trigger
   * the shader to update and apply the new masking effect on the point clouds.
   */
  useEffect(() => {
    updateMask(workingMask ? [workingMask] : [])
  }, [workingMask, updateMask])

  /**
   * Handle working mask changes - update data texture with new region data.
   *
   * The actual change actually comes from document event 'editor:working-cuboid:change'
   * but we're listening to workingMask as well to note when it's cleared (set to null)
   * so we can update the texture accordingly.
   */
  useEffect(() => {
    const handleWorkingMaskChange = (event: Event) => {
      if (!(event instanceof CustomEvent)) return
      const updatedCuboid = event.detail as Cuboid
      const updatedMask: MaskRegion = {
        region_id: 'working-mask',
        ...updatedCuboid,
      }

      updateMask([updatedMask])
    }

    document.addEventListener('editor:working-cuboid:change', handleWorkingMaskChange)
    return () => {
      document.removeEventListener('editor:working-cuboid:change', handleWorkingMaskChange)
    }
  }, [updateMask])

  /**
   * Render loop to trigger Potree's update function.
   * This is necessary to process Potree's LOD and masking updates.
   * Throttled to 10 FPS to balance performance and responsiveness.
   */
  const render = useThrottledCallback(() => {
    if (!pointClouds?.length || isPointCloudInvisible) return
    potree.updatePointClouds(pointClouds, camera, gl)
  }, 1000 / 10)
  useFrame(render)

  if (!pointClouds?.length) return null

  return pointClouds.map((pointCloud) => (
    <primitive key={`point-cloud-${pointCloud.id}`} object={pointCloud} visible={!isPointCloudInvisible} />
  ))
}

export default PotreePCD
